import { useEffect, useState } from "react";
import { Banknote, Sparkles, CheckCircle2, Lock, TrendingUp, X, ArrowRight, Search, Loader2, AlertTriangle, CreditCard } from "lucide-react";
import api, { fmtNaira, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

export default function Loans() {
  const { user } = useAuth();
  const t = useT(user?.preferred_language);
  const [offer, setOffer] = useState(null);
  const [activeLoan, setActiveLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccept, setShowAccept] = useState(false);
  const [pickedSchedule, setPickedSchedule] = useState("daily");

  const load = async () => {
    try {
      const [o, a] = await Promise.all([api.get("/loan-offer"), api.get("/loans/active")]);
      setOffer(o.data);
      setActiveLoan(a.data.loan);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-6 text-center text-kudi-ink/50">{t("checkingOffer")}</div>;

  return (
    <div className="px-4 py-5 space-y-5 pb-6">
      <div>
        <div className="text-xs text-kudi-ink/55 uppercase tracking-wider font-semibold">{t("loansSubtitle")}</div>
        <h1 className="font-heading text-2xl font-bold mt-1">{t("loansTitle")}</h1>
        <p className="text-sm text-kudi-ink/60 mt-1">{t("workingCapital")}</p>
      </div>

      {activeLoan ? (
        <ActiveLoanCard loan={activeLoan} onReload={load} />
      ) : !offer.eligible ? (
        <NotEligibleCard offer={offer} />
      ) : (
        <OfferCard offer={offer} onAccept={(scheduleKey) => { setPickedSchedule(scheduleKey); setShowAccept(true); }} />
      )}

      {/* Auto-debit mandate — required before disbursement */}
      <MandateCard />

      <TrustList />

      {showAccept && <AcceptLoanFlow offer={offer} scheduleKey={pickedSchedule} onClose={() => setShowAccept(false)} onDone={() => { setShowAccept(false); load(); }} />}
    </div>
  );
}

function NotEligibleCard({ offer }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-6 text-center" data-testid="not-eligible">
      <div className="w-14 h-14 rounded-2xl bg-accent text-kudi-ink/50 flex items-center justify-center mx-auto mb-3">
        <Lock className="w-6 h-6" />
      </div>
      <div className="font-heading font-semibold">Almost there!</div>
      <p className="text-sm text-kudi-ink/60 mt-1 mb-4 max-w-xs mx-auto">{offer.reason}</p>
      <div className="bg-accent rounded-full h-2 max-w-xs mx-auto overflow-hidden">
        <div className="bg-kudi-green h-full transition-all" style={{ width: `${offer.progress_pct}%` }} />
      </div>
      <div className="text-xs text-kudi-ink/55 mt-2 tabular-nums">{offer.progress_pct}% — {fmtNaira(offer.sales_30d)} tracked</div>
    </div>
  );
}

function OfferCard({ offer, onAccept }) {
  const schedules = offer.schedules || [
    { key: "daily", label: "Daily", installments: 30, installment_amount: offer.daily_repayment, cadence_days: 1 },
  ];
  const [selected, setSelected] = useState(schedules[0]?.key || "daily");
  const chosen = schedules.find((s) => s.key === selected) || schedules[0];

  return (
    <div className="rounded-2xl bg-gradient-to-br from-kudi-green via-kudi-green-dark to-kudi-green text-white p-6 relative overflow-hidden" data-testid="loan-offer-card">
      <div className="absolute inset-0 ankara-watermark opacity-10" aria-hidden />
      <div className="relative">
        <div className="flex items-center gap-2 text-xs text-white/80 uppercase tracking-wider font-semibold">
          <Sparkles className="w-3.5 h-3.5" /> Personalized for you
        </div>
        <div className="font-heading text-5xl font-bold mt-2 tabular-nums">{fmtNaira(offer.offer_amount)}</div>
        <div className="text-sm text-white/80 mt-1">Available right now</div>

        {/* Schedule picker */}
        <div className="mt-5">
          <div className="text-[10px] text-white/70 uppercase tracking-wider font-semibold mb-2">Repayment schedule</div>
          <div className="grid grid-cols-3 gap-2" data-testid="schedule-picker">
            {schedules.map((s) => (
              <button
                key={s.key}
                onClick={() => setSelected(s.key)}
                data-testid={`schedule-${s.key}`}
                className={`rounded-xl py-2 px-1 text-xs font-semibold border transition ${
                  selected === s.key
                    ? "bg-white text-kudi-green border-white"
                    : "bg-white/10 text-white/90 border-white/25 hover:bg-white/20"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
          <Box label="Interest" value={`${offer.interest_pct}%`} />
          <Box label="Installments" value={`${chosen.installments}`} />
          <Box label={chosen.installments === 1 ? "Repay" : `Per ${chosen.cadence_days === 1 ? "day" : chosen.cadence_days === 7 ? "week" : "month"}`} value={fmtNaira(chosen.installment_amount)} />
        </div>

        <button
          onClick={() => onAccept(selected)}
          className="mt-5 w-full bg-white text-kudi-green rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-cream transition active:scale-[0.98]"
          data-testid="accept-loan-btn"
        >
          <Banknote className="w-4 h-4" /> Accept & get cash
        </button>
      </div>
    </div>
  );
}

function ActiveLoanCard({ loan, onReload }) {
  const [charging, setCharging] = useState(false);
  const repaidPct = Math.min(100, (loan.amount_repaid / loan.total_repayment) * 100);
  const remaining = loan.total_repayment - loan.amount_repaid;
  const installmentAmount = loan.installment_amount ?? loan.daily_repayment;
  const cadenceLabel = loan.schedule_label
    || (loan.schedule === "weekly" ? "Weekly" : loan.schedule === "monthly" ? "Monthly" : "Daily");
  const perLabel = loan.schedule === "weekly" ? "Weekly repay" : loan.schedule === "monthly" ? "Final repay" : "Daily repay";

  const setupRepayment = async () => {
    try {
      const r = await api.post(`/loans/${loan.id}/setup-repayment`);
      window.open(r.data.authorization_url, "_blank");
      toast.success("Complete the ₦50 setup charge in the new tab.");
    } catch (e) {
      toast.error("Failed to start setup");
    }
  };

  const chargeToday = async () => {
    setCharging(true);
    try {
      const r = await api.post(`/loans/${loan.id}/charge-today`);
      if (r.data.success) {
        toast.success(`₦${r.data.amount_charged.toLocaleString()} charged. ${r.data.completed ? "Loan fully repaid! 🎉" : ""}`);
        await onReload();
      } else {
        toast.error(r.data.message || "Charge failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCharging(false); }
  };

  return (
    <div className="space-y-3" data-testid="active-loan">
      <div className="rounded-2xl bg-gradient-to-br from-kudi-green via-kudi-green-dark to-kudi-green text-white p-6 relative overflow-hidden">
        <div className="absolute inset-0 ankara-watermark opacity-10" aria-hidden />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="text-xs text-white/80 uppercase tracking-wider font-semibold">Active loan</div>
            <div className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
              loan.transfer_status === "success" ? "bg-emerald-400/20 text-emerald-100" :
              loan.transfer_status === "failed" ? "bg-rose-400/20 text-rose-100" :
              "bg-amber-400/20 text-amber-100"
            }`}>
              {loan.transfer_status === "success" ? "✓ Disbursed" :
               loan.transfer_status === "failed" ? "Disbursement failed" :
               "Pending disbursement"}
            </div>
          </div>
          <div className="font-heading text-4xl font-bold mt-2 tabular-nums">{fmtNaira(loan.amount)}</div>
          <div className="text-sm text-white/75 mt-1">to {loan.account_name} · {loan.bank_name}</div>

          <div className="mt-5">
            <div className="flex justify-between text-xs text-white/75 mb-1">
              <span>Repaid {fmtNaira(loan.amount_repaid)} of {fmtNaira(loan.total_repayment)}</span>
              <span className="tabular-nums">{repaidPct.toFixed(0)}%</span>
            </div>
            <div className="bg-white/15 rounded-full h-2 overflow-hidden">
              <div className="bg-white h-full transition-all" style={{ width: `${repaidPct}%` }} />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <Box label="Remaining" value={fmtNaira(remaining)} />
            <Box label={perLabel} value={fmtNaira(installmentAmount)} />
          </div>
          <div className="mt-2 text-[11px] text-white/70 uppercase tracking-wider font-semibold" data-testid="active-loan-schedule">{cadenceLabel} schedule</div>
        </div>
      </div>

      {loan.transfer_status === "failed" && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900">
          <strong>Heads up:</strong> {loan.transfer_message || "Transfer failed."} (In test mode, Paystack Transfers may need to be manually enabled on the merchant dashboard.)
        </div>
      )}

      {/* Repayment setup / charge */}
      {!loan.authorization_code ? (
        <button onClick={setupRepayment} className="w-full bg-white border border-kudi-green/40 text-kudi-green rounded-2xl py-3 font-semibold flex items-center justify-center gap-2 hover:bg-accent transition" data-testid="setup-repayment-btn">
          <CreditCard className="w-4 h-4" /> Setup auto-repayment (₦50 verify)
        </button>
      ) : (
        <button onClick={chargeToday} disabled={charging} className="w-full bg-kudi-green text-white rounded-2xl py-3 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-green-dark transition disabled:opacity-50" data-testid="charge-today-btn">
          {charging ? <><Loader2 className="w-4 h-4 animate-spin" /> Charging...</> : <>Pay {cadenceLabel.toLowerCase()} ₦{installmentAmount.toLocaleString()}</>}
        </button>
      )}
    </div>
  );
}

function AcceptLoanFlow({ offer, scheduleKey, onClose, onDone }) {
  const schedules = offer.schedules || [];
  const selectedSchedule = schedules.find((s) => s.key === scheduleKey) || schedules[0] || { key: "daily", installments: 30, installment_amount: offer.daily_repayment, cadence_days: 1, label: "Daily" };
  const [step, setStep] = useState("bank"); // bank | confirm | submitting | done
  const [banks, setBanks] = useState([]);
  const [bankFilter, setBankFilter] = useState("");
  const [selectedBank, setSelectedBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [resolving, setResolving] = useState(false);
  const [accountName, setAccountName] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data.banks)).catch(() => toast.error("Failed to load banks"));
  }, []);

  useEffect(() => {
    setAccountName(null);
    if (selectedBank && accountNumber.length === 10) {
      setResolving(true);
      api.post("/banks/resolve", { account_number: accountNumber, bank_code: selectedBank.code })
        .then((r) => setAccountName(r.data.account_name))
        .catch((e) => {
          setAccountName(null);
          toast.error(e.response?.data?.detail || "Could not verify account");
        })
        .finally(() => setResolving(false));
    }
  }, [selectedBank, accountNumber]);

  const submit = async () => {
    setSubmitting(true);
    setStep("submitting");
    try {
      const r = await api.post("/loans/accept", {
        amount: offer.offer_amount,
        account_number: accountNumber,
        account_name: accountName,
        bank_code: selectedBank.code,
        bank_name: selectedBank.name,
        schedule: selectedSchedule.key,
      });
      setResult(r.data);
      setStep("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
      setStep("confirm");
    } finally { setSubmitting(false); }
  };

  const filteredBanks = banks.filter((b) => {
    const q = bankFilter.toLowerCase().trim();
    if (!q) return true;
    if (b.name.toLowerCase().includes(q)) return true;
    return (b.aliases || []).some((a) => a.toLowerCase().includes(q));
  }).slice(0, 30);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={step === "submitting" ? undefined : onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-6 max-h-[92dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="accept-loan-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">
            {step === "bank" && "Where should we send the cash?"}
            {step === "confirm" && "Confirm details"}
            {step === "submitting" && "Sending your cash..."}
            {step === "done" && "Loan accepted! 🎉"}
          </h2>
          {step !== "submitting" && (
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center" data-testid="close-loan-modal"><X className="w-4 h-4" /></button>
          )}
        </div>

        {step === "bank" && (
          <div className="space-y-3" data-testid="bank-step">
            <div>
              <label className="text-xs font-medium text-kudi-ink/70">Bank</label>
              {!selectedBank ? (
                <>
                  <div className="mt-1.5 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kudi-ink/40" />
                    <input
                      type="text" value={bankFilter} onChange={(e) => setBankFilter(e.target.value)}
                      placeholder="Search bank (e.g. GTBank, Opay, Kuda)..."
                      className="w-full bg-accent border-transparent rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-kudi-green/20"
                      data-testid="bank-search"
                    />
                  </div>
                  <div className="mt-2 max-h-60 overflow-y-auto border border-border rounded-xl divide-y divide-border/40" data-testid="bank-list">
                    {filteredBanks.map((b) => (
                      <button key={b.code} onClick={() => setSelectedBank(b)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent transition" data-testid={`bank-${b.code}`}>
                        {b.name}
                      </button>
                    ))}
                    {filteredBanks.length === 0 && <div className="px-3 py-4 text-sm text-kudi-ink/50">No banks found</div>}
                  </div>
                </>
              ) : (
                <div className="mt-1.5 flex items-center justify-between bg-accent rounded-xl px-3 py-2.5" data-testid="selected-bank">
                  <span className="text-sm font-medium">{selectedBank.name}</span>
                  <button onClick={() => setSelectedBank(null)} className="text-xs text-kudi-green font-semibold">Change</button>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-kudi-ink/70">Account number</label>
              <input
                type="text" inputMode="numeric" maxLength={10} value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="10-digit account number"
                className="mt-1.5 w-full bg-accent border-transparent rounded-xl px-3 py-2.5 text-base font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-kudi-green/20"
                data-testid="account-number-input"
                disabled={!selectedBank}
              />
              {resolving && <div className="mt-2 text-xs text-kudi-ink/60 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Verifying account...</div>}
              {accountName && (
                <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm flex items-center gap-2" data-testid="resolved-account">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="font-semibold">{accountName}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => setStep("confirm")}
              disabled={!selectedBank || !accountNumber || accountNumber.length !== 10 || !accountName}
              className="w-full bg-kudi-green text-white rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-green-dark transition disabled:opacity-40 mt-2"
              data-testid="continue-to-confirm"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-4" data-testid="confirm-step">
            <div className="bg-accent rounded-2xl p-4 space-y-2">
              <Row label="Loan amount" value={fmtNaira(offer.offer_amount)} bold />
              <Row label="Interest (15%)" value={fmtNaira(offer.offer_amount * 0.15)} />
              <Row label="Total to repay" value={fmtNaira(offer.offer_amount * 1.15)} />
              <Row label="Schedule" value={selectedSchedule.label} />
              <Row label="Repayment" value={selectedSchedule.installments === 1 ? `${fmtNaira(selectedSchedule.installment_amount)} in 30 days` : `${fmtNaira(selectedSchedule.installment_amount)} × ${selectedSchedule.installments}`} />
              <div className="border-t border-border/60 pt-2 mt-2">
                <Row label="To bank" value={selectedBank.name} />
                <Row label="Account" value={`${accountName} · ${accountNumber}`} small />
              </div>
            </div>
            <button onClick={submit} className="w-full bg-kudi-green text-white rounded-full py-3.5 font-semibold hover:bg-kudi-green-dark transition active:scale-[0.98]" data-testid="confirm-loan-btn">
              Send my ₦{offer.offer_amount.toLocaleString()}
            </button>
            <button onClick={() => setStep("bank")} className="w-full text-sm text-kudi-ink/60 hover:text-kudi-green py-2">Edit details</button>
          </div>
        )}

        {step === "submitting" && (
          <div className="py-12 text-center">
            <Loader2 className="w-10 h-10 mx-auto text-kudi-green animate-spin" />
            <div className="mt-4 font-heading font-semibold">Initiating Paystack transfer...</div>
            <div className="text-sm text-kudi-ink/60 mt-1">Please don't close this window</div>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-4 text-center" data-testid="loan-done">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div>
              <div className="font-heading text-xl font-bold">{fmtNaira(result?.amount)} on the way!</div>
              <div className="text-sm text-kudi-ink/60 mt-1">
                {result?.transfer_status === "success" || result?.transfer_status === "pending"
                  ? "Funds will reflect in your account within minutes."
                  : "Transfer is queued — Paystack Transfers must be enabled on your merchant account in test mode. The loan is logged and tracked."}
              </div>
            </div>
            <button onClick={onDone} className="w-full bg-kudi-green text-white rounded-full py-3.5 font-semibold hover:bg-kudi-green-dark transition" data-testid="loan-done-btn">
              View my loan
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Box({ label, value }) {
  return (
    <div className="bg-white/10 backdrop-blur border border-white/15 rounded-xl p-3">
      <div className="text-[10px] text-white/70 uppercase tracking-wider">{label}</div>
      <div className="font-heading font-bold mt-0.5 tabular-nums text-sm">{value}</div>
    </div>
  );
}

function Row({ label, value, bold, small }) {
  return (
    <div className={`flex justify-between items-center ${small ? "text-xs" : "text-sm"}`}>
      <span className="text-kudi-ink/65">{label}</span>
      <span className={`tabular-nums ${bold ? "font-heading font-bold" : "font-medium"} text-right`}>{value}</span>
    </div>
  );
}

function TrustList() {
  return (
    <div className="bg-white border border-border rounded-2xl p-5 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center shrink-0">
          <TrendingUp className="w-4 h-4" />
        </div>
        <div>
          <div className="font-heading font-semibold text-sm">Based on your real cashflow</div>
          <div className="text-xs text-kudi-ink/65 mt-0.5">Not collateral. Not stress. Just your sales.</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4" />
        </div>
        <div>
          <div className="font-heading font-semibold text-sm">Disbursed via Paystack in minutes</div>
          <div className="text-xs text-kudi-ink/65 mt-0.5">Direct to your bank account. No paperwork.</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent text-kudi-green flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div>
          <div className="font-heading font-semibold text-sm">Pay back, get more</div>
          <div className="text-xs text-kudi-ink/65 mt-0.5">Each loan you repay unlocks bigger offers.</div>
        </div>
      </div>
    </div>
  );
}


function MandateCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState(false);

  const load = async () => {
    try { const r = await api.get("/payments/mandate/status"); setStatus(r.data); } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setup = async () => {
    setSetting(true);
    try {
      const r = await api.post("/payments/setup-mandate");
      if (r.data.already_set) {
        toast.success("Card already saved");
        load();
        return;
      }
      if (r.data.authorization_url) {
        // Paystack hosted checkout collects card details, saves authorization on webhook.
        window.location.href = r.data.authorization_url;
        return;
      }
      toast.success("Card saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start card setup");
    } finally { setSetting(false); }
  };

  if (loading) return null;
  if (status?.has_mandate) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3" data-testid="mandate-card-active">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-emerald-900">Auto-debit is set up ✓</div>
          <div className="text-[11px] text-emerald-800/80 mt-0.5">
            Card ending {status.card_last4 || "••••"}
            {status.card_bank && <> · {status.card_bank}</>}
            {" · "} we&apos;ll charge your daily repayment automatically.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-border rounded-2xl p-4" data-testid="mandate-card-setup">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
          <CreditCard className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Enable auto-repay to unlock loans</div>
          <div className="text-[11px] text-kudi-ink/60 mt-0.5">
            Save a card once (₦50 verification charge, refunded). Every daily repayment charges automatically —
            no manual work, no missed payments.
          </div>
          <button onClick={setup} disabled={setting}
            className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="setup-mandate-btn">
            {setting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            {setting ? "Starting…" : "Set up auto-repay"}
          </button>
        </div>
      </div>
    </div>
  );
}

